import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { verifyAuth, removeToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const setUser = useCallback((u) => {
    setUserState(u);
  }, []);

  const loadUser = useCallback(async () => {
    if (!getToken()) {
      setUserState(null);
      setAuthLoading(false);
      return;
    }
    try {
      const data = await verifyAuth();
      const perms = data.permissions || {};
      setUserState({
        email: data.email,
        nombre: data.nombre ?? data.email?.split("@")[0] ?? "Usuario",
        color: data.color ?? "#3b82f6",
        avatar_variant: data.avatar_variant ?? "marble",
        is_admin: data.is_admin === true,
        permissions: {
          candidatos: perms.candidatos !== false,
          formularios: perms.formularios !== false,
          revisiones: perms.revisiones !== false && perms.formularios !== false,
          proveedores: perms.proveedores !== false,
          archivos: perms.archivos !== false,
          metricas: perms.metricas === true,
          correos: perms.correos !== false,
          onboarding: perms.onboarding !== false,
          gmb: perms.gmb !== false,
          material: perms.material !== false,
          clientes: perms.clientes !== false,
          chat: perms.chat !== false,
          admin: perms.admin === true,
        },
      });
    } catch (_) {
      setUserState(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    removeToken();
    setUserState(null);
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loadUser,
        logout,
        authLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
